import {
    BonusType,
    getTaxBrackets,
    parseSuperBonusBreakpoints,
    SuperBonusCalculationBase,
    TaxBase,
    TaxMode,
    TicketType,
} from '@superbet-group/betting.lib.payments';
import {
    HandlingFeeMethod,
    IntroPageRetentionVisibility,
    IntroPageType,
    ProductConfig,
    ServerConfig,
    StaticConfig,
    WinTaxMethod,
} from '@superbet-group/web.lib.config';

import { logger } from '@app/logger';
import { getMultiDomainField } from '@app/utils/domainHelpers';
import { OrderDirection } from '@core/constants';
import { ConfigDto } from '@core/mappers/country/dtos/ConfigDto';
import { parseRemoteSuperBonusValidBetTypes } from '@src/config/shell/remoteConfigUtils';
import { ProductType } from '@superbet-group/web.types';

// eslint-disable-next-line sonarjs/cognitive-complexity
export function transformServerConfig(dto: ConfigDto, staticConfig: StaticConfig): ServerConfig {
    let webBackend = dto.newWebBackend ? dto.newWebBackend : dto.webBackend;
    let pushNotificationsUrl = dto.pushNotificationsUrl;

    // Napoleon uses multiple domains, so we need a config object that maps Site to the correct API
    if (dto.webBackendMultiDomain) {
        webBackend = getMultiDomainField(dto.webBackendMultiDomain);
    }

    // TODO: Remove when PL betler migration is done
    if (window.location.hostname.includes('dev-be.superbet.pl')) {
        webBackend = 'https://api.web.dev01.betler.superbet.pl';
    }

    if (dto.pushNotificationsUrlMultiDomain) {
        pushNotificationsUrl = getMultiDomainField(dto.pushNotificationsUrlMultiDomain);
    }

    return <ServerConfig>{
        deposit: {
            onlineDefault: parseInt(dto.depositOnlineDefaultValue, 10),
            onlineMin: parseInt(dto.depositMinOnline, 10),
            onlineMax: parseInt(dto.depositMaxOnline, 10),
            tax: dto.depositTaxLimits,
            quickAmounts: parseListOfInts(dto.depositQuickAmounts),
            depositAircashConfig: dto.depositAircashConfig || null,
        },
        withdrawal: {
            bankTransferMin: dto.withdrawalLimitBankTransfer,
            bankTransferMax: dto.withdrawalLimitBankTransferMax,
            onlineMin: dto.withdrawalLimitOnline,
            onlineMax: dto.withdrawalLimitOnlineMax,
            shopMin: dto.withdrawalLimitShop,
            shopMax: dto.withdrawalLimitShopMax,
            instantMin: dto.withdrawalLimitShop,
            instantMax: dto.quickWithdrawalLimitShop,
            tax: dto.withdrawalTaxLimits || '',
            withdrawalLimitWithoutKycMax: dto.withdrawalLimitWithoutKycMax,
            pixDepositRequiredBeforeWithdrawal: dto.pixDepositRequiredBeforeWithdrawal === '1',
            faceIdRequiredBeforeWithdrawal: dto.faceIdRequiredBeforeWithdrawal === 'true',
        },
        wallet: {
            bankAccountIdFormat: dto.bankAccountIdFormat,
            bankBranchIdFormat: dto.bankBranchIdFormat,
            numberOfBankAccountsToShowBankManagement: dto.numberOfBankAccountsToShowBankManagement,
        },
        idCardVerification: {
            period: dto.daysToKyc,
        },
        applicationStatus: {
            maintenanceFrameUrl: dto.outsourcedMaintenanceFrameUrl,
            serviceMessage: dto.serviceMessage,
            serviceMessageObject: dto._localizedServiceMessage,
        },
        betSlip: {
            stakeMax: parseInt(dto.maxSportStake, 10),
            stakeMin: parseFloat(dto.minSportStake),
            minStakePerCombination: dto.minSportStakePerCombination ? parseFloat(dto.minSportStakePerCombination) : 0.01,
            defaultStake: dto.defaultSportStake ? parseFloat(dto.defaultSportStake) : parseFloat(dto.minSportStake), // NOTE: default stake not used
            maxWin: parseInt(dto.maxWin, 10),
            offlineTax: parseInt(dto.taxOffline, 10),
            onlineTax: parseInt(dto.taxOnline, 10),
            winTaxBounds: parseTaxLevels(dto.winTax),
            minOddBonus: parseFloat(dto.minOddBonus),
            maxBonusPercent: parseInt(dto.maxBonusPercent, 10),
            handlingFeeMethod: parseInt(dto.stateCalculation, 10) as HandlingFeeMethod,
            winTaxMethod: dto.winTaxMethod // NOTE: Not sent
                ? dto.winTaxMethod as WinTaxMethod
                : WinTaxMethod.OneStep,
            handlingFeeInverse: Number(dto.stateCalculation) === HandlingFeeMethod.TaxOnGross,
            taxPerSystem: staticConfig.countrySpecific.multiStepTaxForSystemTickets,
            taxMode: dto.winTaxMethod === WinTaxMethod.MultiStep ? TaxMode.Progressive : TaxMode.Flat,
            taxBase: dto.winTaxMethod === WinTaxMethod.MultiStep ? TaxBase.BaseMinusStake : TaxBase.Base,
            taxBrackets: getTaxBrackets(dto.winTax),
            areSuperBonusSpecialsValidOnline: dto.superBonusSpecialsValidOnline === 'true',
            bonusConfig: {
                bonusType: BonusType.SuperBonus,
                superBonusEnabled: dto.superBonusEnabled === 'true',
                calculationBase: parseRemoteSuperBonusCalculationBase(dto.superBonusCalculationBase),
                validBetTypes: parseRemoteSuperBonusValidBetTypes(dto.superBonusValidBetTypes),
                bonusBreakpoints: dto.superBonusBreakpoints
                    ? parseSuperBonusBreakpoints(dto.superBonusBreakpoints)
                    : [],
                minOddValue: dto.superBonusMinCoef
                    ? Number(dto.superBonusMinCoef)
                    : 0,
                validSelectionTypes: dto.superBonusValidSelectionTypes
                    ? dto.superBonusValidSelectionTypes.split('|')
                    : [],
                validTicketTypes: dto.superBonusValidTicketTypes
                    ? parseValidTicketTypes(dto.superBonusValidTicketTypes)
                    : [],
                ignoredOfferTypes: dto.superBonusIgnoredOfferTypes
                    ? dto.superBonusIgnoredOfferTypes.split('|')
                    : [],
                specialsAllowed: true,
                areSelectionsBelowMinOddTolerated: safeParseBoolean(dto.superBonusSelectionsBelowMinOddTolerated),
            },
            predefinedStakes: parsePredefinedStakes(dto.sportPredefinedStakes),
            maxSelections: dto.sportMaxSelections ? parseInt(dto.sportMaxSelections, 10) : 30,
        },
        // TODO: Remove from config and then clear out from the mapper
        lottoBetSlip: {
            stakeMax: parseInt(dto.maxLotoStake, 10),
            stakeMin: parseInt(dto.minLotoStake, 10),
            maxWin: parseInt(dto.maxWinLotto, 10),
            maxSelectedNumbers: parseInt(dto.maxTicketNumLotto, 10),
            offlineTax: parseInt(dto.taxOffline, 10),
            onlineTax: parseInt(dto.taxOnline, 10),
            handlingFeeInverse: parseInt(dto.stateCalculation, 10) === HandlingFeeMethod.TaxOnGross,
            taxMode: dto.winTaxMethod === WinTaxMethod.MultiStep ? TaxMode.Progressive : TaxMode.Flat,
            taxBase: dto.winTaxMethod === WinTaxMethod.MultiStep ? TaxBase.BaseMinusStake : TaxBase.Base,
            taxBrackets: getTaxBrackets(dto.winTax),
            predefinedStakes: parsePredefinedStakes(dto.lottoPredefinedStakes),
        },
        tickets: {
            paymentHost: dto.legacyWebBackend ? dto.legacyWebBackend : webBackend,
        },
        addressConfig: {
            viacepBaseUrl: 'https://viacep.com.br',
            apicepBaseUrl: 'https://ws.apicep.com',
        },
        offer: {
            hostServer: dto.offerServer, // Use 'https://dev-offer.superbet.ro/' to test.
            socketServer: dto.socketIo, // Use 'https://dev-offer.superbet.ro/' to test.
            visualisation: {
                sevenScoutCheckCoverageUrl: window.location.hostname === 'localhost'
                    ? ''
                    : dto.sevenScoutCheckCoverageUrl,
                sevenScoutCheckCoverageUUID: dto.sevenScoutCheckCoverageUUID,
                sevenScoutSupportedSports: dto.sevenScoutSupportedSports,
                sevenScoutVisualizationActive: dto.sevenScoutVisualizationActive === '1',
                sevenScoutUrl: dto.sevenScoutUrl,
            },
            // hostServer: 'https://offer.superbet.ro'
            additionalInfo: {
                saApiKey: dto.saApiKey,
            },
        },
        // TODO: Remove from config and then clear out from the mapper
        lotto: {
            hostServer: dto.lottoServer || dto.offerServer,
            useFederationLotto: dto.useFederationLotto ?? false,
        },
        auth: {
            host: webBackend,
            isAwsWafIntegrationEnabled: dto.isAwsWafIntegrationEnabled ?? false,
            awsWafUrl: dto.awsWafUrl || '',
            awsWafFallbackUrl: dto.awsWafFallbackUrl || '',
        },
        user: {
            host: webBackend,
            arePushNotificationsEnabled: dto.pushNotificationsEnabled === 'true',
            pushNotificationsUrl,
        },
        country: {
            host: webBackend,
            zendeskKey: dto.zendeskKey,
        },
        bonus: {
            host: webBackend,
        },
        promotion: {
            host: webBackend,
        },
        antiFraud: {
            host: webBackend,
        },
        // this is unused and not existent. Should be added
        newsHost: dto.news,
        general: {
            /*  to be in same format as
                https://developer.mozilla.org/en-US/docs/
                Web/JavaScript/Reference/Global_Objects/Date/getTimezoneOffset
            */
            timeZoneOffset: -(dto.tzOffset / 60),
        },
        virtual: {
            baseUrl: 'https://seven-web-superbet.7platform.com/#',
            virtualFootballUrl: 'https://sp-staging.nsoft.com/vflmstaging/vflm/index?clientid=3482&lang=ro',
        },
        testingAccounts: {
            accounts: dto.testingAccounts
                ? dto.testingAccounts.split(',').reduce(
                    (a: Record<number, boolean>, item: string) => {
                        // eslint-disable-next-line no-param-reassign
                        a[parseInt(item, 10)] = true;
                        return a;
                    },
                    {},
                )
                : {},
        },
        cashout: {
            cashoutBackend: dto.cashoutBackend,
            cashoutFrontendEnabled: parseInt(dto.cashoutFrontendEnabled, 10) === 1
                && staticConfig.countrySpecific.services.cashout.isAvailable,
        },
        documentUpload: {
            fileSizeHumanReadable: `${dto.documentUploadFileSize}MB`,
            fileSize: parseInt(dto.documentUploadFileSize, 10) * 1024 * 1024,
        },
        superbets: {
            superbetsRange: dto.superbetsRange ? dto.superbetsRange.split(',').map((i) => parseInt(i)) as [number, number] : null,
            isNewDesignEnabled: dto.superbetsIsNewDesignEnabled === 'true',
            isMarketTypesFilterEnabled: dto.superbetsIsMarketFilterEnabled === 'true',
            numberOfFeaturedSuperbets: dto.superbetsHighlightsCountWeb ? parseInt(dto.superbetsHighlightsCountWeb) : null,
            sortDirection: dto.superbetsSortType ? dto.superbetsSortType as OrderDirection : null,
            superBetsGroupId: null,
            oddsRangeValues: dto.superbetsOddsRangeValues ? dto.superbetsOddsRangeValues.split(',').map(parseFloat) : null,
        },
        retail: {
            script: dto.retailScript,
        },
        superSpin: {
            superSpinTermsUrl: dto.superSpinTermsUrl,
            superSpinUrl: dto.superSpinUrl,
            superSpinUrlV2: dto.superSpinUrlV2,
        },
        bingoBash: {
            bingoBashTermsUrl: dto.bingoBashTermsUrl,
            bingoBashUrl: dto.bingoBashUrl,
        },
        games: {
            homeConfig: dto.casinoHomepageLayout || null,
            skywindUrl: dto.skywindUrl,
            skywindFeatures: dto.skywindFeatures,
            gamesPersonalisationEnabled: dto.gamesPersonalisationEnabled === 'true',
            liveDealerEnabled: dto.liveDealerEnabled === 'true',
            liveCasinoProxyUrl: dto.casinoLiveStateAPI,
            emergencyShowCasinoLive: dto.emergencyShowCasinoLive === 'true',
            gamingIntegrationsApiUrl: dto.gamingIntegrationsServer,
        },
        superBonus: {
            isEnabled: dto.superBonusEnabled === 'true',
            numberOfSelectionsMinimum: dto.superBonusMinSelections
                ? parseInt(dto.superBonusMinSelections, 10)
                : 0,
            isDoubleGenerosityDescriptionVisible: !!dto.isDoubleGenerosityDescriptionVisible,
        },
        iovation: {
            isEnabled: dto.useIovation,
        },
        migratedSpecials: {
            isEnabled: dto.migratedSpecialsEnabled === 'true',
            marketGroupIds: new Set(parseListOfInts(dto.migratedSpecialsMarketGroups)),
            message: dto.migratedSpecialsMessage?.trim(),
        },
        calendarGame: {
            calendarGameUrl: dto.adventCalendarUrl,
            calendarGameTermsUrl: dto.adventCalendarTermsUrl,
            calendarGameWebPath: dto.calendarGameWebPath ?? 'calendar-game',
        },
        bonusGalaxy: {
            bonusGalaxyUrl: dto.bonusGalaxyUrl,
            bonusGalaxyTermsUrl: dto.bonusGalaxyTermsUrl,
        },
        productConfig: dto.productConfig ? parseProductConfig(dto.productConfig) : {} as any, // TODO: Temporary, remove this completely when federations stop using it
        superMillion: {
            url: dto.superMillionUrl ?? '',
        },
        superProno: {
            url: dto.superPronoUrl ?? '',
        },
        cms: {
            baseUrl: dto.CMSBaseURL ?? '',
        },
        responsibleGaming: {
            shopsWithdrawalThreshold: dto.shopsRGWithdrawalThreshold ?? 10,
            instantWithdrawalThreshold: dto.instantRGWithdrawalThreshold ?? 20,
        },
        ftpIntegrationLayer: {
            baseUrl: dto.ftpIntegrationBaseUrl ?? '',
        },
        loyaltyIntegration: {
            baseUrl: dto.loyaltyIntegrationBaseUrl ?? '',
        },
        superShot: {
            url: dto.superShotUrl ?? '',
        },
        fantasticDuels: {
            url: dto.fantasticDuelsUrl ?? '',
        },
        tvChannels: {
            host: dto.tvChannelsHost,
            iconHost: dto.tvChannelsIconHost ?? '',
            country: dto.tvChannelsCountry ?? '',
            region: dto.tvChannelsRegion ?? '',
        },
        printPdfUrls: {
            outrights: dto.outrightsPrintPdfUrl,
            host: dto.sportsOfferPrintPdfHost,
        },
        sportsOffer: {
            hostV2: `${dto.offerServer}/v2`,
            host: dto.sportsOfferHost ?? '',
            sseUrl: dto.offerStreamingHost ?? '',
            sseUrlV2: `${dto.offerServer}/subscription/v2`,
            isRetailFrontendEnabled: dto.isRetailFrontendEnabled ?? false,
            /**
             * @deprecated Use the `sportOfferConfig` instead
             */
            combinableMarketGroupId: dto.combinableMarketGroupId ? parseInt(dto.combinableMarketGroupId, 10) : undefined,
        },
        liveCasino: {
            // default to true so .COM isn't affected by no remote config
            isEnabled: dto.isLiveCasinoEnabled ?? true,
            usersWhoSeeLiveCasino: new Set(
                dto.usersWhoSeeLiveCasino?.split(',')
                    .map((userId) => parseInt(userId, 10)),
            ),
        },
        seoEngineConfig: {
            url: dto.seoEngineConfigUrl ?? '',
        },
        getIdFlowName: dto.getIdFlowName,
        isUsingNewSportsNavigationPercentageRollout: dto.isUsingNewSportsNavigationPercentageRollout,
        isUsingNewSportsNavigation: dto.isUsingNewSportsNavigation || false,
        isUsingNewTopLevelNavigationPercentageRollout: dto.isUsingNewTopLevelNavigationPercentageRollout,
        isUsingNewTopLevelNavigation: dto.isUsingNewTopLevelNavigation || false,
        newTopLevelNavigationPercentageRollout: dto.newTopLevelNavigationPercentageRollout,
        marketSearchPercentageRollout: dto.marketSearchPercentageRollout,
        analytics: {
            ...dto.analytics,
            disabledEvents: dto.analytics?.disabledEvents
                ? new Set(dto.analytics?.disabledEvents) : new Set(),
        },
        introPageSetup: parseIntroPageSetup(dto.introPageSetup),
    };
}

// this is the value in romanian CMS. It has no tax but sends this string
const defaultTaxLevel = '0|1';

// Tax limits should be in format 'minAmount|tax|minAmount|tax...'
// eslint-disable-next-line class-methods-use-this
function parseTaxLevels(taxLimits: string): {
    lowerBound: number;
    percentage: number;
}[] {
    if (!taxLimits || taxLimits === defaultTaxLevel) {
        return [];
    }
    const limits = taxLimits.split('|').map(parseFloat);
    // handle odd limits length. Should never happen
    const maxLength = (limits.length % 2) ? limits.length - 1 : limits.length;
    if (limits.length % 2) {
        logger.error(new Error('Tax limits have invalid format'));
    }
    const limitsList: { lowerBound: number; percentage: number }[] = [];
    for (let i = 0; i < maxLength - 1; i += 2) {
        limitsList.push({
            lowerBound: limits[i],
            percentage: limits[i + 1],
        });
    }
    return limitsList;
}

// eslint-disable-next-line class-methods-use-this
function parseValidTicketTypes(types: string): TicketType[] {
    try {
        return types.split('|').map((type) => {
            if (type === 'online') {
                return TicketType.online;
            } else if (type === 'retail') {
                return TicketType.retail;
            }
            throw new Error('invalid format');
        });
    } catch (e) {
        logger.error(
            new Error(
                '[Superbonus] ticket types have wrong format: ' + types,
            ),
        );
        return [];
    }
}

// This function is a compat layer so that old CMS (string-only) works
// as well as Betler or Directus data
function safeParseBoolean(dtoValue?: string | boolean): boolean {
    return dtoValue !== undefined && ['true', true].includes(dtoValue);
}

/*
 * Maps Directus config (case-insensitive) to `SuperBonusCalculationBase` enum used by `betting.lib.payments`.
 */
function parseRemoteSuperBonusCalculationBase(base?: string): SuperBonusCalculationBase {
    const baseToLower = base?.toLowerCase();
    switch (baseToLower) {
        case SuperBonusCalculationBase.Profit.toLowerCase():
            return SuperBonusCalculationBase.Profit;
        case SuperBonusCalculationBase.Winning.toLowerCase():
            return SuperBonusCalculationBase.Winning;
        default:
            logger.error(new Error(`The SuperBonus calculation base ${base} is not supported. Using default value 'Winning'`));
            return SuperBonusCalculationBase.Winning;
    }
}

function parseListOfInts(input?: string): number[] {
    return input
        ? input
            .split(',')
            .map((value) => parseInt(value, 10))
        : [];
}

function parseProductConfig(enabledProductsString: string): ProductConfig {
    const enabledProducts = new Set(enabledProductsString.split(','));
    return Object.values(ProductType).reduce((map: ProductConfig, productType) => {
        // eslint-disable-next-line no-param-reassign
        map[productType] = enabledProducts.has(productType);
        return map;
    }, {} as ProductConfig);
}

function parsePredefinedStakes(predefinedStakes?: string): number[] {
    if (!predefinedStakes) {
        return [];
    }

    return predefinedStakes
        .split(',')
        .map((stake) => parseInt(stake, 10));
}

function parseIntroPageSetup(field?: Record<string, string> | undefined): ServerConfig['introPageSetup'] {
    const defaultSetup: ServerConfig['introPageSetup'] = {
        type: IntroPageType.Disabled,
        acquisitionPageName: 'intro',
        retentionPageName: '',
        retentionVisibilityLogic: IntroPageRetentionVisibility.ReturningUserState,
    };

    return {
        ...defaultSetup,
        ...field,
        type: isInEnum(field?.type, IntroPageType) ? field?.type as IntroPageType : defaultSetup.type,
        retentionVisibilityLogic: isInEnum(field?.retentionVisibilityLogic, IntroPageRetentionVisibility) ? field?.retentionVisibilityLogic as IntroPageRetentionVisibility : defaultSetup.retentionVisibilityLogic,
    };
}

function isInEnum<T extends Record<string, string>>(
    value: any,
    enumType: T,
): value is T[keyof T] {
    return value != null && value in enumType;
}
